import { useEffect, useState } from "react";
import { Save, Trash2, Plus, Truck } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import ImageUpload from "@/components/ImageUpload";
import AiWriter from "@/components/admin/AiWriter";

const EMPTY = { name: "", description: "", price: 0, stock: 0, image_url: "", badge: "", slug: "", sizes: ["S", "M", "L", "XL", "XXL", "3XL", "4XL", "5XL"], flocage_price: 10, show_stock: true, allow_backorder: false };

const inputCls = "w-full rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

function ShippingBlock() {
  const [modes, setModes] = useState([]);
  const [freeFrom, setFreeFrom] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/shipping").then((r) => { setModes(r.data.modes || []); setFreeFrom(r.data.free_from || 0); setLoaded(true); }).catch((e) => toast.error(errMsg(e)));
  }, []);

  const setMode = (i, patch) => setModes(modes.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));

  const save = async () => {
    try {
      await api.put("/admin/shipping", { modes, free_from: freeFrom });
      toast.success("Modes de livraison enregistrés");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!loaded) return null;

  return (
    <div className="card-tactical grid gap-4 p-5" data-testid="admin-shipping-section">
      <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
        <Truck className="h-5 w-5 text-orange-500" /> Modes de livraison & tarifs
      </p>
      <p className="text-xs text-slate-500">
        Proposés au client dans le panier ; le tarif choisi s'ajoute au total de la commande.
      </p>
      {modes.map((m, i) => (
        <div key={i} className="grid gap-3 sm:grid-cols-[1fr_110px_180px_auto] sm:items-end" data-testid={`admin-shipping-row-${i}`}>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
            Libellé
            <input value={m.label} data-testid={`admin-shipping-label-${i}`}
              onChange={(e) => setMode(i, { label: e.target.value })} className={inputCls + " mt-1"} />
          </label>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
            Prix (€)
            <input type="number" step="0.10" min="0" value={m.price} data-testid={`admin-shipping-price-${i}`}
              onChange={(e) => setMode(i, { price: +e.target.value || 0 })} className={inputCls + " mt-1"} />
          </label>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
            Délai affiché
            <input value={m.delay} placeholder="3 à 5 jours ouvrés" data-testid={`admin-shipping-delay-${i}`}
              onChange={(e) => setMode(i, { delay: e.target.value })} className={inputCls + " mt-1"} />
          </label>
          <button onClick={() => setModes(modes.filter((_, idx) => idx !== i))} data-testid={`admin-shipping-delete-${i}`} aria-label="Supprimer le mode"
            className="rounded-lg border border-red-500/40 p-2.5 text-red-400 transition-all hover:bg-red-500/10">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <div className="flex flex-wrap items-end gap-4 border-t border-orange-500/10 pt-4">
        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Livraison offerte à partir de (€ — 0 = désactivé)
          <input type="number" step="1" min="0" value={freeFrom} data-testid="admin-shipping-free-from"
            onChange={(e) => setFreeFrom(+e.target.value || 0)} className={inputCls + " mt-1 w-48"} />
        </label>
        <p className="pb-2 text-[11px] text-slate-500">Le client voit une barre de progression « plus que X € » dans son panier.</p>
      </div>
      <div className="flex flex-wrap gap-3">
        <button onClick={() => setModes([...modes, { key: "", label: "", price: 0, delay: "" }])} data-testid="admin-shipping-add"
          className="flex items-center gap-2 rounded-lg border border-orange-500/40 px-4 py-2 text-sm font-bold uppercase text-orange-400 transition-all hover:bg-orange-500/10">
          <Plus className="h-4 w-4" /> Ajouter un mode
        </button>
        <button onClick={save} data-testid="admin-shipping-save"
          className="flex items-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
          <Save className="h-4 w-4" /> Enregistrer les tarifs
        </button>
      </div>
    </div>
  );
}

export default function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [newProduct, setNewProduct] = useState(EMPTY);

  const load = () => api.get("/products").then((r) => {
    setProducts(r.data);
    setDrafts(Object.fromEntries(r.data.map((p) => [p.id, { ...p }])));
  }).catch((e) => toast.error(errMsg(e)));

  const [teasers, setTeasers] = useState([]);
  const [teaserDrafts, setTeaserDrafts] = useState({});
  const [newTeaser, setNewTeaser] = useState({ name: "", note: "Prochainement", image_url: "" });

  const loadTeasers = () => api.get("/teasers").then((r) => {
    setTeasers(r.data);
    setTeaserDrafts(Object.fromEntries(r.data.map((t) => [t.id, { ...t }])));
  }).catch((e) => toast.error(errMsg(e)));

  useEffect(() => { load(); loadTeasers(); }, []);

  const saveTeaser = async (id) => {
    try {
      await api.put(`/admin/teasers/${id}`, teaserDrafts[id]);
      toast.success("Teaser mis à jour");
      loadTeasers();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeTeaser = async (id) => {
    try {
      await api.delete(`/admin/teasers/${id}`);
      toast.success("Teaser supprimé");
      loadTeasers();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const createTeaser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/teasers", newTeaser);
      toast.success("Teaser ajouté à la boutique");
      setNewTeaser({ name: "", note: "Prochainement", image_url: "" });
      loadTeasers();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const save = async (id) => {
    try {
      const d = drafts[id];
      await api.put(`/admin/products/${id}`, { ...d, price: +d.price, stock: +d.stock, flocage_price: +d.flocage_price });
      toast.success("Produit mis à jour");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Produit supprimé");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/products", { ...newProduct, price: +newProduct.price, stock: +newProduct.stock, flocage_price: +newProduct.flocage_price });
      toast.success("Produit créé");
      setNewProduct(EMPTY);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const setDraft = (id, patch) => setDrafts({ ...drafts, [id]: { ...drafts[id], ...patch } });

  return (
    <div className="grid gap-6" data-testid="products-admin">
      <ShippingBlock />
      {products.map((p) => {
        const d = drafts[p.id] || p;
        return (
          <div key={p.id} data-testid={`admin-product-${p.slug || p.id}`} className="card-tactical grid gap-4 p-5 sm:grid-cols-[1fr_auto_auto_auto] sm:items-end">
            <div>
              <p className="font-heading text-lg font-bold uppercase">{p.name}</p>
              <p className="text-xs text-slate-500">{p.badge}</p>
              <div className="mt-3 grid gap-3">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  Prix (€)
                  <input type="number" step="0.01" value={d.price} onChange={(e) => setDraft(p.id, { price: e.target.value })} className={inputCls + " mt-1"} />
                </label>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Stock par taille</p>
                  <div className="mt-1 grid grid-cols-4 gap-2 sm:grid-cols-8">
                    {(d.sizes || []).map((s) => (
                      <label key={s} className="text-center text-[11px] font-bold text-slate-400">
                        {s}
                        <input type="number" min="0" value={(d.stock_by_size || {})[s] ?? 0}
                          data-testid={`admin-stock-input-${p.slug || p.id}-${s}`}
                          onChange={(e) => setDraft(p.id, { stock_by_size: { ...(d.stock_by_size || {}), [s]: Math.max(0, +e.target.value || 0) } })}
                          className={inputCls + " mt-1 text-center"} />
                      </label>
                    ))}
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500">
                    Total : {Object.values(d.stock_by_size || {}).reduce((a, b) => a + (+b || 0), 0)} exemplaire(s)
                  </p>
                </div>
                <div className="grid gap-2 border-t border-orange-500/10 pt-3">
                  <label className="flex items-center gap-3 text-xs font-semibold text-slate-300">
                    <input type="checkbox" checked={d.show_stock !== false} data-testid={`admin-show-stock-${p.slug || p.id}`}
                      onChange={(e) => setDraft(p.id, { show_stock: e.target.checked })}
                      className="h-4 w-4 accent-orange-500" />
                    Afficher les compteurs de stock sur la boutique
                  </label>
                  <label className="flex items-center gap-3 text-xs font-semibold text-slate-300">
                    <input type="checkbox" checked={!!d.allow_backorder} data-testid={`admin-backorder-${p.slug || p.id}`}
                      onChange={(e) => setDraft(p.id, { allow_backorder: e.target.checked })}
                      className="h-4 w-4 accent-orange-500" />
                    Précommande : vendable même en rupture de stock (expédié après réassort)
                  </label>
                </div>
              </div>
            </div>
            <button onClick={() => save(p.id)} data-testid={`admin-stock-save-button-${p.slug || p.id}`}
              className="flex items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
              <Save className="h-4 w-4" /> Enregistrer
            </button>
            <button onClick={() => remove(p.id)} data-testid={`admin-product-delete-${p.slug || p.id}`}
              className="flex items-center justify-center gap-2 rounded-lg border border-red-500/40 px-4 py-2.5 text-sm font-bold uppercase text-red-400 transition-all hover:bg-red-500/10">
              <Trash2 className="h-4 w-4" /> Supprimer
            </button>
          </div>
        );
      })}

      <form onSubmit={create} className="card-tactical grid gap-4 p-5" data-testid="admin-new-product-form">
        <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase"><Plus className="h-5 w-5 text-orange-500" /> Nouveau produit</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <input placeholder="Nom du produit" value={newProduct.name} data-testid="admin-new-product-name"
            onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} className={inputCls} required />
          <input placeholder="Identifiant (ex: hoodie-2026)" value={newProduct.slug}
            onChange={(e) => setNewProduct({ ...newProduct, slug: e.target.value })} className={inputCls} />
          <input type="number" step="0.01" placeholder="Prix (€)" value={newProduct.price || ""} data-testid="admin-new-product-price"
            onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })} className={inputCls} required />
          <input type="number" min="0" placeholder="Stock initial" value={newProduct.stock || ""} data-testid="admin-new-product-stock"
            onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })} className={inputCls} required />
          <div>
            <input placeholder="URL de l'image" value={newProduct.image_url}
              onChange={(e) => setNewProduct({ ...newProduct, image_url: e.target.value })} className={inputCls} />
            <div className="mt-2 flex flex-wrap gap-2">
              <ImageUpload testid="admin-product-image-upload" onUploaded={(url) => setNewProduct((p) => ({ ...p, image_url: url }))} />
            </div>
          </div>
          <input placeholder="Badge (ex: ÉDITION LIMITÉE)" value={newProduct.badge}
            onChange={(e) => setNewProduct({ ...newProduct, badge: e.target.value })} className={inputCls} />
        </div>
        <div>
          <textarea placeholder="Description" value={newProduct.description}
            onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })} className={inputCls} rows={2} />
          <div className="mt-2">
            <AiWriter kind="product" testid="admin-product-ai" buttonLabel="Générer la description avec l'IA"
              context={{ name: newProduct.name, badge: newProduct.badge }}
              onUse={async ({ text }) => {
                setNewProduct((p) => ({ ...p, description: text }));
                toast.success("Description générée");
              }} />
          </div>
        </div>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-3 text-xs font-semibold text-slate-300">
            <input type="checkbox" checked={newProduct.show_stock} data-testid="admin-new-product-show-stock"
              onChange={(e) => setNewProduct({ ...newProduct, show_stock: e.target.checked })}
              className="h-4 w-4 accent-orange-500" />
            Afficher les compteurs de stock
          </label>
          <label className="flex items-center gap-3 text-xs font-semibold text-slate-300">
            <input type="checkbox" checked={newProduct.allow_backorder} data-testid="admin-new-product-backorder"
              onChange={(e) => setNewProduct({ ...newProduct, allow_backorder: e.target.checked })}
              className="h-4 w-4 accent-orange-500" />
            Précommande (vendable même en rupture)
          </label>
        </div>
        <button type="submit" data-testid="admin-new-product-submit"
          className="w-fit rounded-lg bg-orange-500 px-5 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
          Créer le produit
        </button>
      </form>

      <div className="card-tactical grid gap-4 p-5" data-testid="admin-teasers-section">
        <p className="font-heading text-lg font-bold uppercase">Encarts « Prochainement » de la boutique</p>
        {teasers.map((t) => {
          const d = teaserDrafts[t.id] || t;
          return (
            <div key={t.id} data-testid={`admin-teaser-row-${t.id}`} className="grid gap-3 sm:grid-cols-[1fr_180px_1fr_auto_auto] sm:items-end">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
                Nom
                <input value={d.name} data-testid={`admin-teaser-name-${t.id}`}
                  onChange={(e) => setTeaserDrafts({ ...teaserDrafts, [t.id]: { ...d, name: e.target.value } })} className={inputCls + " mt-1"} />
              </label>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
                Mention
                <input value={d.note} placeholder="Prochainement" data-testid={`admin-teaser-note-${t.id}`}
                  onChange={(e) => setTeaserDrafts({ ...teaserDrafts, [t.id]: { ...d, note: e.target.value } })} className={inputCls + " mt-1"} />
              </label>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  Image (URL)
                  <input value={d.image_url || ""} placeholder="https://..." data-testid={`admin-teaser-image-${t.id}`}
                    onChange={(e) => setTeaserDrafts({ ...teaserDrafts, [t.id]: { ...d, image_url: e.target.value } })} className={inputCls + " mt-1"} />
                </label>
                <div className="mt-2">
                  <ImageUpload testid={`admin-teaser-upload-${t.id}`}
                    onUploaded={(url) => setTeaserDrafts((td) => ({ ...td, [t.id]: { ...td[t.id], image_url: url } }))} />
                </div>
              </div>
              <button onClick={() => saveTeaser(t.id)} data-testid={`admin-teaser-save-${t.id}`}
                className="flex items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
                <Save className="h-4 w-4" /> Enregistrer
              </button>
              <button onClick={() => removeTeaser(t.id)} data-testid={`admin-teaser-delete-${t.id}`} aria-label="Supprimer le teaser"
                className="flex items-center justify-center gap-2 rounded-lg border border-red-500/40 px-4 py-2.5 text-sm font-bold uppercase text-red-400 transition-all hover:bg-red-500/10">
                <Trash2 className="h-4 w-4" /> Supprimer
              </button>
            </div>
          );
        })}
        <form onSubmit={createTeaser} className="grid gap-3 border-t border-orange-500/10 pt-4 sm:grid-cols-[1fr_180px_1fr_auto] sm:items-end" data-testid="admin-new-teaser-form">
          <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
            Nouvel encart
            <input placeholder="Ex : Veste Pro 2026" value={newTeaser.name} data-testid="admin-new-teaser-name"
              onChange={(e) => setNewTeaser({ ...newTeaser, name: e.target.value })} className={inputCls + " mt-1"} required />
          </label>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
            Mention
            <input placeholder="Prochainement" value={newTeaser.note} data-testid="admin-new-teaser-note"
              onChange={(e) => setNewTeaser({ ...newTeaser, note: e.target.value })} className={inputCls + " mt-1"} />
          </label>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
              Image (URL, optionnel)
              <input placeholder="https://..." value={newTeaser.image_url} data-testid="admin-new-teaser-image"
                onChange={(e) => setNewTeaser({ ...newTeaser, image_url: e.target.value })} className={inputCls + " mt-1"} />
            </label>
            <div className="mt-2">
              <ImageUpload testid="admin-new-teaser-upload" onUploaded={(url) => setNewTeaser((t) => ({ ...t, image_url: url }))} />
            </div>
          </div>
          <button type="submit" data-testid="admin-new-teaser-submit"
            className="flex items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
            <Plus className="h-4 w-4" /> Ajouter
          </button>
        </form>
      </div>
    </div>
  );
}
